/**
 * RhfDocumentList — Generic form-engine adapter for session-scoped document lists.
 *
 * Renders a list of document rows managed by any DocumentListStore implementation.
 * Matches legacy `ImagePreview` row styling: full-width rows with document icon,
 * filename, and delete button.
 *
 * The store is resolved at runtime from DocumentListStoreRegistry using the
 * `storeKey` prop (passed via `adapterProps.storeKey` in model.json).
 *
 * When `filterGroup` is set, only items whose `groupKey` matches are displayed.
 * This enables per-button document lists (interleaved layout matching legacy).
 *
 * @example model.json — single group filter
 *   "grantAgreementDocs": {
 *     "$ref": "module://plan-management#properties/grantAgreementDocs",
 *     "adapterProps": {
 *       "storeKey": "GrantDocumentStore",
 *       "filterGroup": "Grant Agreement Document"
 *     }
 *   }
 */

import * as React from 'react';
import styled from 'styled-components';
import { rem } from '../tokens/spacing';
import { palette } from '../tokens/palette';
import { DocumentListStoreRegistry } from '@packages/shared/document-list/registry';
import type { DocumentListItem, DocumentListStore } from '@packages/shared/document-list/contract';
import { buildDocumentListGroups, hasDocumentListItems } from '@packages/shared/document-list/grouping';
import {
  DOCUMENT_STORE_DIRTY_FIELD,
  clearDocumentStoreBaseline,
  establishDocumentStoreBaseline,
  evaluateDocumentStoreDirtySync,
  getDocumentStoreBaseline,
} from '@packages/shared/document-list/dirtySync';
import type { FieldValues, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { ActionRegistry } from '@packages/table-builder/registry/ActionRegistry';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RhfDocumentListProps {
  name?: string;
  control?: unknown;
  label?: string;
  disabled?: boolean;
  /**
   * Registry key for the DocumentListStore implementation.
   * @example "GrantDocumentStore"
   */
  storeKey?: string;
  /**
   * When set, only items matching this groupKey are rendered.
   * Enables per-button interleaved document lists (legacy parity).
   * @example "Grant Agreement Document"
   */
  filterGroup?: string;
  /**
   * Optional ordered list of groupKey values to control display order.
   * Only applies when `filterGroup` is NOT set.
   */
  groupOrder?: string[];
  /**
   * When set with `setValue` / `getValues`, selecting inline chips clears the paired
   * plan dropdown field (mutual exclusion with plan document async-select).
   */
  exclusiveDropdown?: string;
  setValue?: UseFormSetValue<FieldValues>;
  getValues?: UseFormGetValues<FieldValues>;
  /** Skip exclusive sync while form is hydrating from API (avoids edit-load false dirty). */
  hydratingRef?: React.RefObject<boolean>;
  /**
   * Form-engine lifecycle status — when transitioning to `hydrated` / `idle`, the
   * document-store baseline is recaptured so edit-load does not mark the form dirty.
   */
  formStatus?: string;
  /**
   * Monotonic counter incremented when form hydration completes (after hydratingRef clears).
   * Used to capture document-store baseline once the store reflects edit-load data.
   */
  hydrationEpoch?: number;
  /** Compose form session bucket — preferred over global document store active session. */
  formSessionKey?: string;
  /** Opens persisted-document edit flow (e.g. OPEN_EDIT_PLAN_GRANT_DOCUMENT). */
  editActionId?: string;
  /** Form field that must be truthy for Edit to show (e.g. allowEditPlanRelatedDocuments). */
  editGateField?: string;
  /** Parent form mode from compose meta — Edit requires `'edit'`. */
  formMode?: string;
  /** Parent context id field on the form (e.g. `"id"`). */
  contextIdField?: string;
  /** Parent context id from compose block params (form-engine sets when model `adapterProps.contextIdParamKey` is set). */
  contextIdFromParams?: string;
  /** Action param name for resolved context id when dispatching `editActionId` (e.g. `"planId"`). */
  editActionContextKey?: string;
  [key: string]: unknown;
}

function readDocumentListItems(
  store: DocumentListStore,
  formSessionKey?: string,
): DocumentListItem[] {
  if (formSessionKey && store.getItemsForSession) {
    return store.getItemsForSession(formSessionKey);
  }
  return store.getItems();
}

// ─── Styled components (legacy ImagePreview parity) ───────────────────────────

/** Outer wrapper — contains the bordered container + all rows */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

/**
 * Bordered container around the document rows.
 * Matches legacy `DocumentContainer`: border, padding, margin.
 */
const DocContainer = styled.div`
  border: ${rem(1)} solid var(--ui-border-subtle, #e8e8e8);
  border-radius: ${rem(4)};
  padding: ${rem(8)} ${rem(8)} ${rem(4)} ${rem(8)};
`;

/**
 * Single document row — matches legacy `ImagePreview`:
 *   - full width, flex row, space-between
 *   - #F3F3F3 background, 7px border-radius, 8px padding
 *   - document icon (38px) + filename (14px gray) on left
 *   - delete icon (red) on right
 */
const DocRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border-radius: ${rem(7)};
  background-color: var(--ui-surface-secondary, #f3f3f3);
  padding: ${rem(8)};
  margin-top: ${rem(8)};

  &:first-child {
    margin-top: 0;
  }
`;

const DocInfo = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  flex: 1;
  min-width: 0;

  .material-symbols-outlined {
    font-size: ${rem(38)};
    line-height: ${rem(38)};
    margin: 0;
    flex-shrink: 0;
    color: var(--ui-icon-default, ${palette.Gray});
  }
`;

const DocName = styled.span`
  font-weight: 400;
  font-size: ${rem(14)};
  color: var(--ui-text-secondary, ${palette.Gray});
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: ${rem(8)};
`;

const DocActions = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${rem(16)};
`;

const IconActionButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(4)};
  background: none;
  border: none;
  cursor: pointer;
  color: var(--ui-text-secondary, ${palette.Gray});
  transition: opacity 150ms ease-in-out;

  &:hover {
    opacity: 0.7;
  }

  .material-symbols-outlined {
    font-size: ${rem(20)};
    line-height: 1;
  }
`;

const DeleteButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(4)};
  background: none;
  border: none;
  cursor: pointer;
  color: var(--ui-error, ${palette.Red});
  transition: opacity 150ms ease-in-out;

  &:hover {
    opacity: 0.7;
  }

  .material-symbols-outlined {
    font-size: ${rem(20)};
    line-height: 1;
  }
`;

// ─── Group helpers ────────────────────────────────────────────────────────────

const GroupLabel = styled.p`
  margin: 0 0 ${rem(4)} 0;
  font-size: ${rem(11)};
  font-weight: 600;
  line-height: 1.4;
  color: var(--ui-text-subtle, ${palette.Gray});
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const GroupWrapper = styled.div`
  margin-bottom: ${rem(8)};
  &:last-child {
    margin-bottom: 0;
  }
`;

function buildGroups(
  items: DocumentListItem[],
  filterGroup?: string,
  groupOrder?: string[],
) {
  return buildDocumentListGroups(items, filterGroup, groupOrder);
}

// ─── Component ───────────────────────────────────────────────────────────────

export const RhfDocumentList = React.memo<RhfDocumentListProps>(
  function RhfDocumentList({
    storeKey,
    filterGroup,
    groupOrder,
    disabled,
    exclusiveDropdown,
    setValue,
    getValues,
    hydratingRef,
    formStatus,
    hydrationEpoch,
    formSessionKey,
    editActionId,
    editGateField,
    formMode,
    contextIdField,
    contextIdFromParams,
    editActionContextKey,
  }) {
    const store = storeKey ? DocumentListStoreRegistry.get(storeKey) : undefined;

    const readItems = React.useCallback((): DocumentListItem[] => {
      if (!store) return [];
      return readDocumentListItems(store, formSessionKey);
    }, [store, formSessionKey]);

    const [items, setItems] = React.useState<DocumentListItem[]>(() => readItems());

    const baselineSnapshotRef = React.useRef<string | null>(null);
    const prevFormStatusRef = React.useRef<string | undefined>(undefined);
    const prevHydrationEpochRef = React.useRef<number | undefined>(undefined);

    const captureDocumentStoreBaseline = React.useCallback((): boolean => {
      if (!store || !setValue || !storeKey) return false;

      const { established, freshCapture } = establishDocumentStoreBaseline({
        items: readItems(),
        formStatus,
        isHydratingRef: hydratingRef?.current,
        storeKey,
        setBaselineSnapshot: (snapshot) => {
          baselineSnapshotRef.current = snapshot;
        },
      });

      if (established && freshCapture) {
        setValue(DOCUMENT_STORE_DIRTY_FIELD, false as never, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }

      return established;
    }, [store, storeKey, setValue, formStatus, hydratingRef, readItems]);

    const syncDocumentStoreDirty = React.useCallback((): void => {
      if (!store || !setValue) return;

      const result = evaluateDocumentStoreDirtySync({
        items: readItems(),
        formStatus,
        isHydratingRef: hydratingRef?.current,
        storeKey,
        baselineSnapshot: baselineSnapshotRef.current,
        setBaselineSnapshot: (snapshot) => {
          baselineSnapshotRef.current = snapshot;
        },
      });

      if (result.resetDirtyField) {
        setValue(DOCUMENT_STORE_DIRTY_FIELD, false as never, {
          shouldDirty: false,
          shouldValidate: false,
        });
        return;
      }

      if (result.shouldMarkDirty) {
        setValue(DOCUMENT_STORE_DIRTY_FIELD, true as never, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    }, [store, storeKey, setValue, formStatus, hydratingRef, readItems]);

    React.useEffect(() => {
      if (!store || !formSessionKey) return;
      const listStore = store as DocumentListStore & {
        setActiveSession?: (key: string) => void;
      };
      listStore.setActiveSession?.(formSessionKey);
    }, [store, formSessionKey]);

    React.useEffect(() => {
      if (!store) return;

      let cancelled = false;
      let rafId = 0;
      let attempts = 0;

      const tryCaptureBaseline = (): void => {
        if (cancelled) return;
        if (captureDocumentStoreBaseline()) return;
        if (hydratingRef?.current && attempts < 20) {
          attempts += 1;
          rafId = requestAnimationFrame(tryCaptureBaseline);
        }
      };

      rafId = requestAnimationFrame(tryCaptureBaseline);

      return () => {
        cancelled = true;
        cancelAnimationFrame(rafId);
      };
    }, [store, captureDocumentStoreBaseline, hydratingRef, hydrationEpoch, formStatus]);

    React.useEffect(() => {
      if (hydrationEpoch === undefined || hydrationEpoch === prevHydrationEpochRef.current) return;
      prevHydrationEpochRef.current = hydrationEpoch;
      if (storeKey) clearDocumentStoreBaseline(storeKey);
      baselineSnapshotRef.current = null;
      captureDocumentStoreBaseline();
    }, [hydrationEpoch, storeKey, captureDocumentStoreBaseline]);

    React.useEffect(() => {
      if (prevFormStatusRef.current === formStatus) return;
      prevFormStatusRef.current = formStatus;
      if (formStatus === 'fetching' && storeKey) {
        clearDocumentStoreBaseline(storeKey);
      }
      if (formStatus === 'fetching') {
        baselineSnapshotRef.current = null;
      }
      if (formStatus === 'hydrated' || formStatus === 'idle') {
        if (storeKey) clearDocumentStoreBaseline(storeKey);
        baselineSnapshotRef.current = null;
        captureDocumentStoreBaseline();
      }
    }, [formStatus, storeKey, captureDocumentStoreBaseline]);

    const syncExclusiveDropdown = React.useCallback((): void => {
      if (hydratingRef?.current) return;
      if (!store || !exclusiveDropdown || !setValue || !getValues) return;

      const storeItems = readItems();
      if (!hasDocumentListItems(storeItems, filterGroup, groupOrder)) return;

      const current = getValues(exclusiveDropdown);
      if (current == null || current === '') return;

      setValue(exclusiveDropdown, '', { shouldDirty: true });
    }, [store, exclusiveDropdown, setValue, getValues, filterGroup, groupOrder, hydratingRef, readItems]);

    React.useEffect(() => {
      if (!store) return;
      setItems(readItems());
      syncDocumentStoreDirty();
      syncExclusiveDropdown();
      return store.subscribe(() => {
        setItems(readItems());
        syncDocumentStoreDirty();
        syncExclusiveDropdown();
      });
    }, [store, syncDocumentStoreDirty, syncExclusiveDropdown, readItems, formSessionKey]);

    const resolveContextId = React.useCallback((): string => {
      const fromField =
        contextIdField && getValues ? getValues(contextIdField as never) : undefined;
      const candidate = fromField ?? contextIdFromParams ?? formSessionKey ?? '';
      return String(candidate ?? '').trim();
    }, [contextIdField, contextIdFromParams, formSessionKey, getValues]);

    const groups = buildGroups(items, filterGroup, groupOrder);

    if (groups.length === 0) return null;

    const canDelete = !disabled;

    const gateValue =
      editGateField && getValues ? getValues(editGateField as never) : undefined;
    const isEditGateOpen =
      !editGateField ||
      gateValue === true ||
      gateValue === 'Yes' ||
      gateValue === 'yes';

    const canEditItem = (item: DocumentListItem): boolean =>
      Boolean(
        editActionId &&
          formMode === 'edit' &&
          item.persistedId &&
          !disabled &&
          isEditGateOpen,
      );

    const handleEdit = (item: DocumentListItem): void => {
      if (!editActionId || !item.persistedId) return;
      const contextId = resolveContextId();
      if (!contextId) return;
      const contextParamKey = editActionContextKey?.trim();
      if (!contextParamKey) return;
      void ActionRegistry.executeAction(editActionId, {
        pk: '',
        row: {},
        refreshTable: () => {},
        params: {
          uniqueDocId: item.uniqueDocId,
          [contextParamKey]: contextId,
        },
      });
    };

    const handleRemove = (uniqueDocId: string): void => {
      store?.removeItem(uniqueDocId, formSessionKey);
      setItems(readItems());
      syncDocumentStoreDirty();
    };

    return (
      <Container>
        {groups.map((group) => (
          <GroupWrapper key={group.key}>
            {group.label && <GroupLabel>{group.label}</GroupLabel>}
            <DocContainer>
              {group.items.map((item) => (
                <DocRow key={item.uniqueDocId}>
                  <DocInfo>
                    <span className="material-symbols-outlined">draft</span>
                    <DocName title={item.label}>{item.label}</DocName>
                  </DocInfo>
                  {(canEditItem(item) || canDelete) && (
                    <DocActions>
                      {canEditItem(item) && (
                        <IconActionButton
                          type="button"
                          title={`Edit ${item.label}`}
                          aria-label={`Edit ${item.label}`}
                          onClick={() => { handleEdit(item); }}
                        >
                          <span className="material-symbols-outlined">edit</span>
                        </IconActionButton>
                      )}
                      {canDelete && (
                        <DeleteButton
                          type="button"
                          title={`Remove ${item.label}`}
                          aria-label={`Remove ${item.label}`}
                          onClick={() => { handleRemove(item.uniqueDocId); }}
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </DeleteButton>
                      )}
                    </DocActions>
                  )}
                </DocRow>
              ))}
            </DocContainer>
          </GroupWrapper>
        ))}
      </Container>
    );
  },
);

RhfDocumentList.displayName = 'RhfDocumentList';
